import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Loader2, RefreshCw, Clock, ShoppingCart } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { SyncTimestamp } from './sync-ui-utils';

export function OrderSyncCard() {
  const [lookbackMinutes, setLookbackMinutes] = useState('');

  const runSync = trpc.organization.runSync.useMutation({
    onSuccess: () => toast.success('Manual order sync started. Check back in a moment.'),
    onError: (err) => toast.error(err.message || 'Failed to start manual sync'),
  });

  const syncState = trpc.shopifyIntegration.getOrderSyncState.useQuery(undefined, {
    refetchInterval: 30_000,
  });

  const handleManualSync = () => {
    const lookback = lookbackMinutes ? parseInt(lookbackMinutes, 10) : undefined;
    runSync.mutate(lookback ? { lookbackMinutes: lookback } : {});
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
            <ShoppingCart className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1">
            <CardTitle className="text-base">Order Sync</CardTitle>
            <CardDescription className="text-xs">
              Syncs orders from Shopify every 5 minutes
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="gap-1">
            <Clock className="h-3 w-3" />
            Every 5 minutes
          </Badge>
          {syncState.data?.last_completed_at && (
            <SyncTimestamp
              timestamp={syncState.data.last_completed_at}
              ordersSynced={syncState.data.orders_synced}
            />
          )}
        </div>

        <div className="flex items-end gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="order-sync-lookback" className="text-xs text-muted-foreground">
              Lookback (minutes)
            </Label>
            <Input
              id="order-sync-lookback"
              type="number"
              min={1}
              max={43200}
              placeholder="Default"
              value={lookbackMinutes}
              onChange={(e) => setLookbackMinutes(e.target.value)}
              className="h-8 w-28"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleManualSync}
            disabled={runSync.isPending}
            className="h-8"
          >
            {runSync.isPending ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                Starting...
              </>
            ) : (
              <>
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                Sync Orders
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
